import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Typography } from '../../../Material-UI/Components';
import { useAppContext } from '../../../store/context';
import AuthShell from '../AuthShell';

const useStyles = makeStyles((theme) => ({
  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  planCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderRadius: 24,
    padding: '22px 20px',
    background: '#f8fbff',
    border: '1px solid rgba(14, 165, 233, 0.14)',
    boxShadow: '0 14px 28px rgba(15, 23, 42, 0.06)',
    minHeight: 360,
  },
  featuredCard: {
    background: 'linear-gradient(180deg, rgba(14,165,233,0.12) 0%, rgba(255,255,255,0.96) 100%)',
    border: '1px solid rgba(14, 165, 233, 0.28)',
    boxShadow: '0 20px 34px rgba(14, 165, 233, 0.16)',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderRadius: 999,
    padding: '6px 10px',
    background: '#e0f2fe',
    color: '#0369a1',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  badgeFeature: {
    background: '#082f49',
    color: '#f0f9ff',
  },
  planTitle: {
    marginTop: 16,
    fontSize: 28,
    lineHeight: 1.1,
    letterSpacing: '-0.04em',
    color: '#0f172a',
    fontWeight: 700,
  },
  planPrice: {
    marginTop: 14,
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
    color: '#0f172a',
  },
  priceValue: {
    fontSize: 42,
    lineHeight: 1,
    letterSpacing: '-0.05em',
    fontWeight: 700,
  },
  priceUnit: {
    fontSize: 14,
    color: '#64748b',
    paddingBottom: 6,
  },
  planBody: {
    marginTop: 14,
    fontSize: 14,
    lineHeight: 1.7,
    color: '#475569',
  },
  featureList: {
    marginTop: 18,
    marginBottom: 0,
    paddingLeft: 18,
    color: '#334155',
    lineHeight: 1.8,
    '& li': {
      marginBottom: 8,
    },
  },
  footerText: {
    marginTop: 18,
    fontSize: 13,
    lineHeight: 1.6,
    color: '#64748b',
  },
  primaryAction: {
    marginTop: '20px !important',
    minHeight: 50,
    borderRadius: 18,
    background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)',
    boxShadow: '0 16px 28px rgba(14, 165, 233, 0.24)',
    color: '#ffffff',
    fontWeight: 700,
    letterSpacing: '0.05em',
    '&:hover': {
      background: 'linear-gradient(135deg, #075985 0%, #0284c7 100%)',
    },
  },
  secondaryAction: {
    marginTop: '20px !important',
    minHeight: 50,
    borderRadius: 18,
    background: '#e2e8f0',
    color: '#0f172a',
    fontWeight: 700,
    '&:hover': {
      background: '#cbd5e1',
    },
  },
  noteCard: {
    marginTop: 18,
    borderRadius: 18,
    border: '1px solid rgba(14, 165, 233, 0.18)',
    background: '#f0f9ff',
    padding: '14px 16px',
  },
  noteLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#0ea5e9',
  },
  noteText: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 1.6,
    color: '#334155',
  },
}));

const plans = [
  {
    id: 'starter',
    badge: 'Get started',
    title: 'Starter',
    price: 'Free',
    unit: '',
    body: 'Explore the workspace, understand your direction, and get a first layer of career guidance.',
    features: [
      'Profile setup and account access',
      'Basic workspace navigation',
      'Initial targeting and career context',
    ],
    footer: 'Best for trying MyJobAide and setting up your first workspace.',
    cta: 'Create free workspace',
    featured: false,
  },
  {
    id: 'pro',
    badge: 'Most aligned',
    title: 'Pro',
    price: '$24',
    unit: '/month',
    body: 'Use the full career operating system across targeting, execution, interview prep, and continuity.',
    features: [
      'Signals, blockers, and strategy guidance',
      'Resume and execution workflows',
      'Interview preparation and AI guidance',
      'Progress continuity across sessions',
    ],
    footer: 'Best for active job search or role-transition work where clarity and execution both matter.',
    cta: 'Choose Pro',
    featured: true,
  },
  {
    id: 'team',
    badge: 'For teams',
    title: 'Team',
    price: '$72',
    unit: '/month',
    body: 'Coordinate premium access across multiple users who need shared structure and repeatable career support.',
    features: [
      'Everything in Pro',
      'Multi-user access patterns',
      'Higher-volume usage support',
      'Better fit for coaching or program environments',
    ],
    footer: 'Best for coordinated career support across a team, cohort, or advisory workflow.',
    cta: 'Talk to us',
    featured: false,
  },
];

const PricingContent = () => {
  const history = useHistory();
  const classes = useStyles();
  const { state } = useAppContext();
  const { email: signedMail } = state.user;
  const isSignedIn = Boolean(signedMail);

  useEffect(() => {
    if (isSignedIn) {
      window.location.replace('/dash/settings/subscription');
    }
  }, [isSignedIn]);

  if (isSignedIn) {
    return null;
  }

  return (
    <AuthShell
      eyebrow="Career Operating System"
      heroTitle="Choose the workspace depth that matches your next move."
      heroSubtitle="MyJobAide is strongest when direction, execution, interview preparation, and continuity work as one connected system."
      heroBadges={[
        {
          label: 'Single career truth',
          value: 'Signals, blockers, strategy',
          hint: 'Use one operating system instead of stitching together resume, interview, and planning tools by hand.',
        },
        {
          label: 'Built for momentum',
          value: 'From targeting to outcomes',
          hint: 'See what changed, what still blocks progress, and what to do next.',
        },
      ]}
      heroQuoteTitle="Why pricing is framed this way"
      heroQuoteBody="You are not just buying a resume tool or a chatbot. You are choosing how much of the MyJobAide career operating system you want actively working for you."
      formEyebrow="Plans"
      formTitle="Pick the MyJobAide plan that fits your journey"
      formSubtitle={
        isSignedIn
          ? 'Review the available plans without leaving your current session. You can compare options here and then return to your workspace.'
          : 'All plans keep the entry flow simple. You can start free, or choose a deeper operating layer if you want more guidance and execution support right away.'
      }
      footerPrompt={isSignedIn ? 'Finished comparing plans?' : 'Already know where you want to start?'}
      footerActionLabel={isSignedIn ? 'Return to workspace' : 'Sign in to your workspace'}
      onFooterAction={() => history.push(isSignedIn ? '/summary' : '/login')}
    >
      <div className={classes.pricingGrid}>
        {plans.map((plan) => (
          <section
            key={plan.id}
            className={[classes.planCard, plan.featured ? classes.featuredCard : ''].filter(Boolean).join(' ')}
          >
            <div>
              <div className={[classes.badge, plan.featured ? classes.badgeFeature : ''].filter(Boolean).join(' ')}>
                {plan.badge}
              </div>
              <div className={classes.planTitle}>{plan.title}</div>
              <div className={classes.planPrice}>
                <span className={classes.priceValue}>{plan.price}</span>
                {plan.unit ? <span className={classes.priceUnit}>{plan.unit}</span> : null}
              </div>
              <div className={classes.planBody}>{plan.body}</div>
              <ul className={classes.featureList}>
                {plan.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>

            <div>
              <Typography className={classes.footerText}>{plan.footer}</Typography>
              <Button
                name={plan.cta}
                type="button"
                className={plan.featured ? classes.primaryAction : classes.secondaryAction}
                fullWidth
                size="large"
                onClick={() => history.push(isSignedIn ? '/summary' : (plan.featured ? '/signup' : '/login'))}
              >
                {isSignedIn ? 'Continue in workspace' : plan.cta}
              </Button>
            </div>
          </section>
        ))}
      </div>

      <div className={classes.noteCard}>
        <div className={classes.noteLabel}>What happens next</div>
        <div className={classes.noteText}>
          Start by creating or reopening your workspace. From there, MyJobAide can connect your profile, targeting, execution work, and interview preparation inside one system.
        </div>
      </div>
    </AuthShell>
  );
};

const Price = () => <PricingContent />;

export default Price;
